import React, { useMemo, useState } from "react";
import api from "../../lib/api";
import {
  CheckCircle2,
  Circle,
  Send,
  Check,
  ShoppingCart,
  PackageCheck,
  Receipt,
  Euro,
  Loader2,
  X,
  XCircle,
} from "lucide-react";

const ETAPES = [
  {
    key: "envoi",
    label: "Devis envoyé",
    date: "date_envoi",
    icon: Send,
    color: "blue",
  },
  {
    key: "acceptation",
    label: "Validé par le client",
    date: "date_acceptation",
    icon: Check,
    color: "green",
  },
  {
    key: "commande",
    label: "Commande passée",
    date: "date_commande",
    icon: ShoppingCart,
    color: "orange",
    reference: "commande_reference",
    commentaire: "commande_commentaire",
  },
  {
    key: "livraison",
    label: "Livraison effectuée",
    date: "date_livraison",
    icon: PackageCheck,
    color: "green",
    reference: "livraison_reference",
    commentaire: "livraison_commentaire",
    applicable: "livraison_applicable",
  },
  {
    key: "facturation",
    label: "Dossier facturé",
    date: "date_facturation",
    icon: Receipt,
    color: "purple",
    reference: "facturation_reference",
    commentaire: "facturation_commentaire",
    applicable: "facturation_applicable",
  },
  {
    key: "encaissement",
    label: "Dossier encaissé",
    date: "date_encaissement",
    icon: Euro,
    color: "green",
    reference: "encaissement_reference",
    commentaire: "encaissement_commentaire",
    applicable: "encaissement_applicable",
  },
];

function formatDate(value) {
  if (!value) return "";
  try {
    return new Intl.DateTimeFormat("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(new Date(value));
  } catch {
    return value;
  }
}

export default function CycleVieDossier({ devis, onUpdated, readOnly = false }) {
  const [open, setOpen] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    date: "",
    reference: "",
    commentaire: "",
    applicable: true,
  });

  const currentIndex = useMemo(() => {
    let idx = -1;
    ETAPES.forEach((e, i) => {
      if (devis?.[e.date]) idx = i;
    });
    return idx;
  }, [devis]);

  const start = (etape) => {
    if (readOnly) return;

    setError("");
    setForm({
      date: etape.date && devis?.[etape.date]
        ? devis[etape.date].slice(0, 10)
        : new Date().toISOString().slice(0, 10),
      reference: etape.reference ? (devis?.[etape.reference] || "") : "",
      commentaire: etape.commentaire ? (devis?.[etape.commentaire] || "") : "",
      applicable: etape.applicable
        ? devis?.[etape.applicable] !== false
        : true,
    });
    setOpen(etape.key);
  };

  const save = async () => {
    if (readOnly) return;
    if (!open) return;

    const etape = ETAPES.find((e) => e.key === open);
    if (!etape) return;

    setSaving(true);
    setError("");

    try {
      const result = await api.post(`/devis/${devis.id}/etape`, {
        etape: etape.key,
        date: form.date
          ? new Date(`${form.date}T12:00:00`).toISOString()
          : "",
        reference: form.reference,
        commentaire: form.commentaire,
        applicable: etape.applicable ? form.applicable : null,
      });

      setOpen(null);

      if (onUpdated) {
        onUpdated(result?.data?.devis || result?.devis);
      }
    } catch (e) {
      setError(
        e?.response?.data?.detail ||
        e?.message ||
        "Impossible d'enregistrer cette étape."
      );
    } finally {
      setSaving(false);
    }
  };

  if (!devis?.id) return null;

  const isRefused = devis?.statut === "Refusé";

  if (isRefused) {
    return (
      <section className="cycle-dossier cycle-dossier-refused">
        <div className="cycle-header">
          <div>
            <div className="cycle-eyebrow">PARCOURS DU DOSSIER</div>
            <h3>Cycle de vie métier</h3>
            <p>
              Le parcours commercial de ce dossier est terminé.
            </p>
          </div>
        </div>

        <div className="cycle-terminal cycle-terminal-refused">
          <div className="cycle-terminal-icon">
            <XCircle size={28} />
          </div>

          <div className="cycle-terminal-content">
            <div className="cycle-terminal-title">
              Dossier refusé — parcours terminé
            </div>

            <div className="cycle-terminal-meta">
              {devis?.date_refus && (
                <span>
                  Refusé le {formatDate(devis.date_refus)}
                </span>
              )}

              {devis?.refuse_par && (
                <span>
                  par {devis.refuse_par}
                </span>
              )}
            </div>

            <p>
              Ce dossier est archivé et n'est plus actif dans le suivi
              commercial. Les relances et tâches ouvertes ont été annulées.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="cycle-dossier">
      <div className="cycle-header">
        <div>
          <div className="cycle-eyebrow">PARCOURS DU DOSSIER</div>
          <h3>Cycle de vie métier</h3>
          <p>
            Chaque étape est horodatée et conservée dans l'historique du dossier.
          </p>
        </div>
      </div>

      <div className="cycle-track">
        {ETAPES.map((etape, index) => {
          const Icon = etape.icon;
          const done = Boolean(devis?.[etape.date]);
          const isNext = !done && index === currentIndex + 1;
          const applicable = etape.applicable
            ? devis?.[etape.applicable] !== false
            : true;

          return (
            <React.Fragment key={etape.key}>
              <div className={`cycle-step ${done ? "done" : ""} ${isNext ? "next" : ""} ${!applicable ? "na" : ""}`}>
                <button
                  type="button"
                  className="cycle-step-main"
                  onClick={() => start(etape)}
                  disabled={readOnly || saving || !applicable}
                >
                  <span className="cycle-icon">
                    {done ? <CheckCircle2 size={19} /> : <Icon size={18} />}
                  </span>

                  <span className="cycle-step-content">
                    <strong>{etape.label}</strong>
                    {done ? (
                      <small>{formatDate(devis[etape.date])}</small>
                    ) : !applicable ? (
                      <small>Non applicable</small>
                    ) : (
                      <small>{isNext ? "À renseigner" : "En attente"}</small>
                    )}
                  </span>
                </button>

                {done && etape.reference && devis[etape.reference] && (
                  <div className="cycle-reference">
                    Réf. {devis[etape.reference]}
                  </div>
                )}
              </div>

              {index < ETAPES.length - 1 && (
                <div className={`cycle-line ${done ? "done" : ""}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {open && (() => {
        const etape = ETAPES.find((e) => e.key === open);
        if (!etape) return null;
        const Icon = etape.icon;

        return (
          <div className="cycle-editor">
            <div className="cycle-editor-head">
              <div className="cycle-editor-title">
                <span className="cycle-icon">
                  <Icon size={18} />
                </span>
                <div>
                  <strong>{etape.label}</strong>
                  <small>Enregistrer cette étape</small>
                </div>
              </div>

              <button type="button" className="cycle-close" onClick={() => setOpen(null)}>
                <X size={18} />
              </button>
            </div>

            <div className="cycle-form">
              <label>
                Date
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                />
              </label>

              {etape.applicable && (
                <label className="cycle-checkbox">
                  <input
                    type="checkbox"
                    checked={form.applicable}
                    onChange={(e) =>
                      setForm({ ...form, applicable: e.target.checked })
                    }
                  />
                  <span>Cette étape est applicable à ce dossier</span>
                </label>
              )}

              {etape.reference && form.applicable && (
                <label>
                  Référence
                  <input
                    type="text"
                    placeholder={
                      open === "commande"
                        ? "N° commande fournisseur"
                        : open === "livraison"
                        ? "N° BL / livraison"
                        : open === "facturation"
                        ? "N° facture"
                        : "Référence encaissement"
                    }
                    value={form.reference}
                    onChange={(e) =>
                      setForm({ ...form, reference: e.target.value })
                    }
                  />
                </label>
              )}

              {etape.commentaire && form.applicable && (
                <label>
                  Commentaire
                  <textarea
                    rows={2}
                    placeholder="Information complémentaire..."
                    value={form.commentaire}
                    onChange={(e) =>
                      setForm({ ...form, commentaire: e.target.value })
                    }
                  />
                </label>
              )}
            </div>

            {error && <div className="cycle-error">{error}</div>}

            <div className="cycle-editor-actions">
              <button
                type="button"
                className="cycle-btn secondary"
                onClick={() => setOpen(null)}
                disabled={saving}
              >
                Annuler
              </button>

              <button
                type="button"
                className="cycle-btn primary"
                onClick={save}
                disabled={saving}
              >
                {saving ? <Loader2 size={16} className="spin" /> : <Check size={16} />}
                {saving ? "Enregistrement..." : "Valider l'étape"}
              </button>
            </div>
          </div>
        );
      })()}

      <div className="cycle-footer">
        <Circle size={13} />
        <span>
          Les étapes validées sont enregistrées automatiquement dans le journal du dossier.
        </span>
      </div>
    </section>
  );
}
